import { useInfiniteQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ClipboardList } from "lucide-react";
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  PageHeader,
  PageSpinner,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
} from "../../../components/ui";
import { useCan } from "../../../features/workspace/workspace-context";
import { useTRPC } from "../../../lib/trpc";
import { formatDateTime, shortId } from "../../../lib/utils";

export const Route = createFileRoute("/w/$slug/audit")({ component: AuditLog });

/** "membership.role_changed" → "Membership role changed" */
/** The screens call these "approved" and "sent"; the event names predate that. */
const ACTION_WORDS: Record<string, string> = {
  "brief.submitted": "Brief sent",
  "brief.accepted": "Brief approved",
  "brief.clarification_requested": "Brief sent back with questions",
};

const describeAction = (action: string) => {
  if (ACTION_WORDS[action]) return ACTION_WORDS[action];
  const text = action.replace(/[._]/g, " ");
  return text.charAt(0).toUpperCase() + text.slice(1);
};

function AuditLog() {
  const allowed = useCan("audit.read");
  if (!allowed)
    return <EmptyState title="The audit log is available to agency owners and admins" />;
  return <AuditTable />;
}

function AuditTable() {
  const trpc = useTRPC();
  const audit = useInfiniteQuery(
    trpc.audit.list.infiniteQueryOptions(
      { limit: 50 },
      { getNextPageParam: (last) => last.nextCursor ?? undefined },
    ),
  );

  return (
    <>
      <PageHeader
        title="Audit log"
        description="Every change in this workspace: who did it, when, and the request it came from."
      />
      <Card>
        {audit.isPending ? (
          <PageSpinner />
        ) : audit.isError ? (
          <ErrorState error={audit.error} onRetry={() => void audit.refetch()} />
        ) : audit.data.pages[0]?.items.length === 0 ? (
          <EmptyState icon={<ClipboardList className="size-8" />} title="No activity yet" />
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>When</TH>
                <TH>Action</TH>
                <TH>Entity</TH>
                <TH>Who</TH>
                <TH>Request</TH>
              </TR>
            </THead>
            <TBody>
              {audit.data.pages.flatMap((page) =>
                page.items.map((entry) => (
                  <TR key={entry.id}>
                    <TD className="whitespace-nowrap text-muted">
                      {formatDateTime(entry.createdAt)}
                    </TD>
                    <TD className="font-medium">{describeAction(entry.action)}</TD>
                    <TD className="text-muted">
                      {entry.entityType.replaceAll("_", " ")}{" "}
                      <span className="font-mono text-xs">…{shortId(entry.entityId)}</span>
                    </TD>
                    <TD className="text-muted">
                      {entry.actorType === "user"
                        ? (entry.actorName ?? "Former member")
                        : entry.actorType}
                    </TD>
                    <TD className="font-mono text-xs text-subtle">
                      {entry.requestId ? `…${shortId(entry.requestId)}` : "—"}
                    </TD>
                  </TR>
                )),
              )}
            </TBody>
          </Table>
        )}
      </Card>
      {audit.hasNextPage && (
        <div className="mt-4 flex justify-center">
          <Button
            variant="secondary"
            loading={audit.isFetchingNextPage}
            onClick={() => void audit.fetchNextPage()}
          >
            Load more
          </Button>
        </div>
      )}
    </>
  );
}
